import { useEffect, useState } from "react";
import { api, type CifFlag, type FunctionPatch, type FunctionRow } from "../api.js";
import type { JSX } from "react";

interface Props {
  registerId: string;
  hasData: boolean;
}

const CIF_FLAG_LABELS: Record<CifFlag, string> = {
  critical: "Critical",
  important: "Important",
  not_critical: "Not critical",
  unassessed: "Unassessed",
};

const CIF_FLAG_OPTIONS: CifFlag[] = ["critical", "important", "not_critical", "unassessed"];

interface EditState {
  functionRef: string;
  cifFlag: CifFlag;
  cifLastAssessedOn: string;
  cifReason: string;
  saving: boolean;
  error: string | null;
}

export function FunctionsPanel({ registerId, hasData }: Props): JSX.Element {
  const [rows, setRows] = useState<FunctionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);

  function load(): void {
    setLoading(true);
    setError(null);
    api
      .listFunctions(registerId)
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, [registerId]); // load redeclared each render; registerId is the real dep

  function openEdit(row: FunctionRow): void {
    setEdit({
      functionRef: row.functionRef,
      cifFlag: row.cifFlag,
      cifLastAssessedOn: row.cifLastAssessedOn ?? "",
      cifReason: row.cifReason ?? "",
      saving: false,
      error: null,
    });
  }

  function saveEdit(): void {
    if (edit === null) return;
    const patch: FunctionPatch = {
      cifFlag: edit.cifFlag,
      cifLastAssessedOn: edit.cifLastAssessedOn === "" ? null : edit.cifLastAssessedOn,
      cifReason: edit.cifReason === "" ? null : edit.cifReason,
    };
    setEdit({ ...edit, saving: true, error: null });
    api
      .patchFunction(registerId, edit.functionRef, patch)
      .then(() => {
        setEdit(null);
        load();
      })
      .catch((e: unknown) => {
        setEdit((prev) =>
          prev
            ? { ...prev, saving: false, error: e instanceof Error ? e.message : String(e) }
            : null,
        );
      });
  }

  const needsReason =
    edit !== null && (edit.cifFlag === "critical" || edit.cifFlag === "important");

  if (!hasData) {
    return (
      <p className="muted" style={{ marginBottom: "1rem" }}>
        No data yet — import and promote arrangements via the Import tab first.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="loading-state">
        <span className="spinner" aria-hidden="true" />
        Loading functions…
      </div>
    );
  }

  if (error !== null) {
    return <div className="error">{error}</div>;
  }

  return (
    <div data-testid="functions-panel">
      <p className="muted" style={{ marginBottom: "0.75rem", fontSize: "0.85rem" }}>
        Set the CIF flag, assessment date, and documented reason for each function. Critical and
        important functions require a reason before submission.
      </p>

      {edit !== null && (
        <div
          style={{
            background: "var(--fog)",
            border: "1px solid var(--line)",
            borderRadius: "10px",
            padding: "16px 20px",
            marginBottom: "1rem",
          }}
        >
          <h3 style={{ margin: "0 0 12px", fontSize: "0.95rem" }}>
            Edit: <code>{edit.functionRef}</code>
          </h3>

          <label className="field">
            CIF Flag
            <select
              value={edit.cifFlag}
              onChange={(e) => setEdit({ ...edit, cifFlag: e.target.value as CifFlag })}
              data-testid="edit-cif-flag"
            >
              {CIF_FLAG_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {CIF_FLAG_LABELS[f]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            Assessment Date
            <input
              type="date"
              value={edit.cifLastAssessedOn}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setEdit({ ...edit, cifLastAssessedOn: e.target.value })}
              data-testid="edit-cif-date"
              style={{
                display: "block",
                margin: "4px 0",
                padding: "0.4rem 0.6rem",
                border: "1px solid var(--line)",
                borderRadius: "6px",
                fontSize: "0.875rem",
                background: "var(--fog)",
                color: "var(--ink)",
                maxWidth: "220px",
              }}
            />
          </label>

          <label className="field">
            Documented Reason
            {needsReason && (
              <span style={{ color: "var(--bad)", marginLeft: "2px" }} aria-label="required">
                *
              </span>
            )}
            <textarea
              rows={3}
              value={edit.cifReason}
              onChange={(e) => setEdit({ ...edit, cifReason: e.target.value })}
              placeholder={
                needsReason
                  ? "Required — explain why this function is critical/important"
                  : "Optional — document the rationale"
              }
              data-testid="edit-cif-reason"
              style={{ marginTop: "4px", fontSize: "0.875rem" }}
            />
          </label>

          {edit.error !== null && <div className="error">{edit.error}</div>}

          <div className="actions">
            <button
              onClick={saveEdit}
              disabled={edit.saving || (needsReason && edit.cifReason.trim() === "")}
              data-testid="edit-save"
            >
              {edit.saving ? "Saving…" : "Save"}
            </button>
            <button className="ghost" onClick={() => setEdit(null)} disabled={edit.saving}>
              Cancel
            </button>
          </div>
          {needsReason && edit.cifReason.trim() === "" && (
            <p style={{ color: "var(--bad)", fontSize: "0.78rem", margin: "4px 0 0" }}>
              Reason is required for critical and important functions.
            </p>
          )}
        </div>
      )}

      <div className="workspace-grid" style={{ overflowX: "auto" }}>
        <table data-testid="functions-table">
          <thead>
            <tr>
              <th>Function Ref</th>
              <th>Name</th>
              <th>CIF Flag</th>
              <th>Assessed On</th>
              <th>Reason</th>
              <th>Provenance</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}
                >
                  No functions. Import data first.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const flagBad =
                  (row.cifFlag === "critical" || row.cifFlag === "important") && !row.cifReason;
                return (
                  <tr key={row.functionRef}>
                    <td>
                      <code style={{ fontSize: "0.82rem" }}>{row.functionRef}</code>
                    </td>
                    <td>{row.name}</td>
                    <td>
                      <span
                        style={{
                          fontWeight: 600,
                          color:
                            row.cifFlag === "critical"
                              ? "var(--bad)"
                              : row.cifFlag === "important"
                                ? "var(--warn)"
                                : "var(--muted)",
                        }}
                      >
                        {CIF_FLAG_LABELS[row.cifFlag]}
                      </span>
                    </td>
                    <td style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                      {row.cifLastAssessedOn ?? "—"}
                    </td>
                    <td
                      style={{
                        maxWidth: "260px",
                        fontSize: "0.82rem",
                        color: flagBad ? "var(--bad)" : "var(--ink)",
                      }}
                    >
                      {row.cifReason ?? (flagBad ? "⚠ Missing — required for submission" : "—")}
                    </td>
                    <td style={{ color: "var(--muted)", fontSize: "0.8rem" }}>{row.provenance}</td>
                    <td>
                      <button
                        className="ghost"
                        onClick={() => openEdit(row)}
                        style={{ padding: "3px 10px", fontSize: "0.8rem" }}
                        data-testid={`edit-btn-${row.functionRef}`}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
